/**
 * Text Primitive
 * Semantic text component with CDS color tokens and typography variants
 */

import React from "react";
import { Text as ChakraText } from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { TextColorToken, InvertedColorToken, Color } from "design-system/foundation/Color";

// Branded types for typography variants
declare const __typography_variant_brand: unique symbol;

export interface TypographyVariantToken {
  readonly [__typography_variant_brand]: true;
  readonly _variant: string;
}

// Factory function
const createTypographyVariantToken = (variant: string): TypographyVariantToken => ({ _variant: variant } as TypographyVariantToken);

export interface TextProps {
  children: React.ReactNode;
  variant?: TypographyVariantToken;
  color?: TextColorToken | InvertedColorToken;
  // Common React props
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  // Event handlers
  onClick?: React.MouseEventHandler<HTMLElement>;
  // Accessibility
  "aria-label"?: string;
  "aria-describedby"?: string;
  role?: string;
}

function TextComponent({
  children,
  variant = textTokens.variant.bodyMedium,
  color = Color.text.primary,
  ...restProps
}: TextProps) {
  // Resolve color using hook for automatic reactivity
  const resolvedColor = useColorModeValue(color);

  // Get typography styles based on variant
  const getTypographyStyles = (variant: TypographyVariantToken) => {
    switch (variant._variant) {
      case "display-large":
        return { fontSize: "4xl", fontWeight: "bold", lineHeight: "shorter" };
      case "display-medium":
        return { fontSize: "3xl", fontWeight: "bold", lineHeight: "shorter" };
      case "display-small":
        return { fontSize: "2xl", fontWeight: "bold", lineHeight: "short" };

      case "headline-large":
        return { fontSize: "xl", fontWeight: "semibold", lineHeight: "shorter" };
      case "headline-medium":
        return { fontSize: "lg", fontWeight: "semibold", lineHeight: "shorter" };
      case "headline-small":
        return { fontSize: "md", fontWeight: "semibold", lineHeight: "short" };

      case "title-large":
        return { fontSize: "lg", fontWeight: "medium", lineHeight: "short" };
      case "title-medium":
        return { fontSize: "md", fontWeight: "medium", lineHeight: "short" };
      case "title-small":
        return { fontSize: "sm", fontWeight: "medium", lineHeight: "short" };

      case "body-large":
        return { fontSize: "md", fontWeight: "normal", lineHeight: "base" };
      case "body-medium":
        return { fontSize: "sm", fontWeight: "normal", lineHeight: "base" };
      case "body-small":
        return { fontSize: "xs", fontWeight: "normal", lineHeight: "base" };

      case "label-large":
        return { fontSize: "sm", fontWeight: "medium", lineHeight: "short" };
      case "label-medium":
        return { fontSize: "xs", fontWeight: "medium", lineHeight: "short" };
      case "label-small":
        return { fontSize: "2xs", fontWeight: "medium", lineHeight: "short" };

      case "caption":
        return { fontSize: "2xs", fontWeight: "normal", lineHeight: "short" };

      case "code-large":
        return { fontSize: "md", fontWeight: "normal", lineHeight: "base", fontFamily: "mono" };
      case "code-medium":
        return { fontSize: "sm", fontWeight: "normal", lineHeight: "base", fontFamily: "mono" };
      case "code-small":
        return { fontSize: "xs", fontWeight: "normal", lineHeight: "base", fontFamily: "mono" };

      default:
        return { fontSize: "sm", fontWeight: "normal", lineHeight: "base" };
    }
  };

  const typographyStyles = getTypographyStyles(variant);

  return (
    <ChakraText
      color={resolvedColor}
      {...typographyStyles}
      {...restProps}
    >
      {children}
    </ChakraText>
  );
}

// Text variant tokens
const textTokens = {
  variant: {
    // Display variants
    displayLarge: createTypographyVariantToken("display-large"),
    displayMedium: createTypographyVariantToken("display-medium"),
    displaySmall: createTypographyVariantToken("display-small"),

    // Headline variants
    headlineLarge: createTypographyVariantToken("headline-large"),
    headlineMedium: createTypographyVariantToken("headline-medium"),
    headlineSmall: createTypographyVariantToken("headline-small"),

    // Title variants
    titleLarge: createTypographyVariantToken("title-large"),
    titleMedium: createTypographyVariantToken("title-medium"),
    titleSmall: createTypographyVariantToken("title-small"),

    // Body variants
    bodyLarge: createTypographyVariantToken("body-large"),
    bodyMedium: createTypographyVariantToken("body-medium"),
    bodySmall: createTypographyVariantToken("body-small"),

    // Label variants
    labelLarge: createTypographyVariantToken("label-large"),
    labelMedium: createTypographyVariantToken("label-medium"),
    labelSmall: createTypographyVariantToken("label-small"),

    // Special variants
    caption: createTypographyVariantToken("caption"),

    // Code variants
    codeLarge: createTypographyVariantToken("code-large"),
    codeMedium: createTypographyVariantToken("code-medium"),
    codeSmall: createTypographyVariantToken("code-small"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Text = Object.assign(TextComponent, textTokens);
